import store from '@/store';
import {
    ActivityProduct,
    FerryLeg,
    FerryPax,
    FerryProduct,
    FlightPax,
    FlightProduct,
    HotelProduct,
    MandatoryField,
    Nationality,
    Pax,
    ProductType,
} from '@/api/Cart/cart.interfaces';
import { isNil } from 'lodash';
import { AnyProduct } from '@shared/interfaces/product';
import { AgeRangeDefinitions } from '@/api/Checkout/checkout.interfaces';
import { getAgeRange } from '@/api/Cart/cart.helpers';
import { isMandatory } from '@shared/helpers/product';
import { FerryVehicle } from '@shared/interfaces/ferry';
import { AdditionalBaggage, Baggage, BaggagePerSector } from '@shared/interfaces/baggage';

// TODO: create tests for copy
export function copyProductsPax<T extends AnyProduct>(source: T, products: T[]): T[] {
    return products
        .filter((p) => p.Id !== source.Id)
        .map((p) => copyProductPax(source, p) as T);
}

export function copyProductPax(source: AnyProduct, target: AnyProduct): AnyProduct {
    switch (source.Type) {
        case ProductType.Hotel:
            return copyHotelPax(source as HotelProduct, target as HotelProduct);
        case ProductType.Flight:
            return copyFlightPax(source as FlightProduct, target as FlightProduct);
        case ProductType.Ferry:
            return copyFerryPax(source as FerryProduct, target as FerryProduct);
        case ProductType.Activity:
            return copyActivityPax(source as ActivityProduct, target as ActivityProduct);
        default:
            return target;
    }
}

function copyHotelPax(source: HotelProduct, target: HotelProduct): HotelProduct {
    const fullCopy = source?.Id === target.Id;

    const updatedProduct: HotelProduct = {
        ...target,
        Rooms: target.Rooms.map((r, i) => ({
            ...r,
            Pax: source.Rooms[i]
                ? {
                      ...source.Rooms[i].Pax,
                      SpecialRequest: fullCopy
                          ? source.Rooms[i].Pax.SpecialRequest
                          : r.Pax.SpecialRequest,
                  }
                : { ...r.Pax },
        })),
    };

    return updatedProduct;
}

function copyFlightPax(source: FlightProduct, target: FlightProduct): FlightProduct {
    const ageDefinitions: AgeRangeDefinitions =
        store.getters['content/getCheckoutAgeRangeDefinitions'];
    const usedPax = new Set<FlightPax>();
    const fullCopy = source?.Id === target.Id;

    const copyBaggageSelection = (
        sourcePax: FlightPax,
        pax: FlightPax
    ): AdditionalBaggage => {
        const getHash = (b: Baggage) => {
            const perSector = (sectors: BaggagePerSector[]) =>
                sectors.map((ss) => ss.SectorNumber).join('|');
            return [
                b.Index,
                b.Type,
                b.Pieces,
                b.Weight,
                b.Price.Amount,
                perSector(b.PerSector ?? []),
            ].join(':');
        };

        const mapBaggage = (baggage: Baggage) => {
            const isSelected = Boolean(
                sourcePax.AdditionalBaggage.Alternatives.find(
                    (b) => getHash(b) === getHash(baggage)
                )?.IsSelected
            );
            return {
                ...baggage,
                IsSelected: isSelected,
            };
        };

        return {
            ...pax.AdditionalBaggage,
            Alternatives: pax.AdditionalBaggage.Alternatives.map(mapBaggage),
        };
    };

    const mapPax = (pax: FlightPax) => {
        const sourcePax = source.Pax.find(
            (p) =>
                !usedPax.has(p) &&
                getAgeRange(source, p, ageDefinitions).Type ===
                    getAgeRange(target, pax, ageDefinitions).Type
        );

        if (sourcePax) {
            usedPax.add(sourcePax);
            return {
                ...pax,
                Gender: sourcePax.Gender,
                FirstName: sourcePax.FirstName,
                LastName: sourcePax.LastName,
                DateOfBirth: copyDateOfBirth(sourcePax, pax),
                Nationality: copyNationality(sourcePax, pax),
                ...(fullCopy && {
                    AdditionalBaggage: copyBaggageSelection(sourcePax, pax),
                }),
                ...(fullCopy && {
                    SelectedSeats: sourcePax.SelectedSeats,
                }),
            };
        }
        return pax;
    };

    const updatedProduct: FlightProduct = {
        ...target,
        Pax: target.Pax.map(mapPax),
    };

    return updatedProduct;
}

function copyFerryPax(source: FerryProduct, target: FerryProduct): FerryProduct {
    const ageDefinitions: AgeRangeDefinitions =
        store.getters['content/getCheckoutAgeRangeDefinitions'];
    const usedPax = new Set<FerryPax>();
    const mapLeg = (leg: FerryLeg, legIndex: number) => {
        return {
            Segments: leg.Segments.map((segment, segmentIndex) => {
                const sourceLegVehicles = source
                    ? source.Legs[legIndex].Segments[segmentIndex].Vehicles
                    : [];

                return {
                    ...segment,
                    Vehicles:
                        !isNil(segment.Vehicles) && !isNil(sourceLegVehicles)
                            ? copyVehicles(sourceLegVehicles, segment.Vehicles)
                            : null,
                };
            }),
        };
    };

    const mapPax = (pax: FerryPax) => {
        const sourcePax = source.Pax.find(
            (p) =>
                !usedPax.has(p) &&
                getAgeRange(source, p, ageDefinitions).Type ===
                    getAgeRange(target, pax, ageDefinitions).Type
        );

        if (sourcePax) {
            usedPax.add(sourcePax);
            return {
                ...pax,
                Gender: sourcePax.Gender,
                FirstName: sourcePax.FirstName,
                LastName: sourcePax.LastName,
                DateOfBirth: copyDateOfBirth(sourcePax, pax),
                Nationality: copyNationality(sourcePax, pax),
                PassportNumber: copyPassportNumber(sourcePax, pax),
                PassportExpirationDate: copyPassportExpiration(sourcePax, pax),
                SpecialNeedsOption: sourcePax.SpecialNeedsOption,
                LoyaltyMembership: copyLoyaltyMembership(sourcePax, pax),
            };
        }
        return pax;
    };

    const updatedProduct: FerryProduct = {
        ...target,
        Legs: target.Legs.map(mapLeg),
        Pax: target.Pax.map(mapPax),
    };
    return updatedProduct;
}

function copyActivityPax(source: ActivityProduct, target: ActivityProduct): ActivityProduct {
    return {
        ...target,
        Tickets: target.Tickets.map((ticket, index) => {
            const sourceTicket = source.Tickets[index];
            const fullCopy = sourceTicket?.Id === ticket.Id;
            if (!sourceTicket) return { ...ticket };

            return {
                ...ticket,
                Pax: fullCopy
                    ? { ...sourceTicket.Pax }
                    : {
                          ...sourceTicket.Pax,
                          TicketFields: ticket.Pax.TicketFields,
                      },
            };
        }),
    };
}

function copyVehicles(source: FerryVehicle[], target: FerryVehicle[]): FerryVehicle[] | null {
    const usedVehicles = new Set<FerryVehicle>();

    const getHash = (vehicle: FerryVehicle) => `${vehicle.Type}|${vehicle.Description}`;

    const mapVehicle = (vehicle: FerryVehicle) => {
        const sourceVehicle = source.find(
            (v) => !usedVehicles.has(v) && getHash(v) === getHash(vehicle)
        );
        if (sourceVehicle) {
            usedVehicles.add(sourceVehicle);
            return { ...vehicle, LicensePlate: sourceVehicle.LicensePlate };
        }
        return vehicle;
    };

    return target.map(mapVehicle);
}

function copyDateOfBirth(source: Pax, target: Pax) {
    return isMandatory(target.MandatoryFields, MandatoryField.DateOfBirth)
        ? source.DateOfBirth
        : target.DateOfBirth;
}

function copyNationality(source: Pax, target: Pax): Nationality {
    const selectedNationality = isMandatory(
        target.MandatoryFields,
        MandatoryField.Nationality
    )
        ? source.Nationality
        : target.Nationality;

    return {
        CountryCode: selectedNationality?.CountryCode ?? '',
        NationalityName: selectedNationality?.NationalityName ?? '',
    };
}

function copyPassportNumber(source: Pax, target: Pax) {
    return isMandatory(target.MandatoryFields, MandatoryField.PassportNumber)
        ? source.PassportNumber
        : target.PassportNumber;
}

function copyPassportExpiration(source: Pax, target: Pax) {
    return isMandatory(target.MandatoryFields, MandatoryField.PassportExpirationDate)
        ? source.PassportExpirationDate
        : target.PassportExpirationDate;
}

function copyLoyaltyMembership(source: FerryPax, target: FerryPax) {
    return isMandatory(target.MandatoryFields, MandatoryField.LoyaltyMembership)
        ? source.LoyaltyMembership
        : target.LoyaltyMembership;
}
